'use client';

import { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/popover';
import { Checkbox } from '@heroui/checkbox';
import { Input } from '@heroui/input';
import { UserIcon, BuildingsIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useCSM } from '@/contexts/CSMContext';
import { FeaturesContext } from '@/app/contexts';

// IMPORTANT: NEXT_PUBLIC_HEIMDALL_URL must be set to the public-facing Heimdall URL to access Tenant Management
const HEIMDALL_URL = process.env.NEXT_PUBLIC_HEIMDALL_URL;
const system_distinguisher = process.env.SYSTEM_DISTINGUISHER || process.env.NEXT_PUBLIC_SYSTEM_DISTINGUISHER || null;
const stage = process.env.SYSTEM_STAGE || process.env.NEXT_PUBLIC_SYSTEM_STAGE || null;
const isDevOrLocal = stage && stage === 'dev';
export function SwitchTenant() {
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const {
    isSuperAdmin,
    currentTenantId,
    currentTenantName,
    currentOrgName,
    isLoadingTenants,
    loadAccessibleTenants,
    accessibleTenants,
    isCSM
  } = useCSM();
  const [isOpen, setIsOpen] = useState(false);
  const [switchingTenantId, setSwitchingTenantId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const handleSwitchTenant = useCallback(async (tenantId: string) => {
    try {
      setSwitchingTenantId(tenantId);
      setError(null);

      // Get the Cognito access token from ALB headers
      const albInfo = await fetch('/api/auth/alb-info');
      const albData = await albInfo.json();

      // Try access token first, fallback to ID token if access token is not available
      const rawAccessToken = albData.tokens?.rawAccessToken;
      const rawIdToken = albData.tokens?.rawIdToken;
      const cognitoAccessToken = rawAccessToken && rawAccessToken.trim() !== '' ? rawAccessToken : rawIdToken && rawIdToken.trim() !== '' ? rawIdToken : null;
      if (!cognitoAccessToken) {
        console.error('[SwitchTenant] No Cognito token available', {
          hasRawAccessToken: !!rawAccessToken,
          rawAccessTokenLength: rawAccessToken?.length || 0,
          rawAccessTokenEmpty: rawAccessToken === '' || rawAccessToken?.trim() === '',
          hasRawIdToken: !!rawIdToken,
          rawIdTokenLength: rawIdToken?.length || 0,
          rawIdTokenEmpty: rawIdToken === '' || rawIdToken?.trim() === '',
          tokenSource: rawAccessToken && rawAccessToken.trim() !== '' ? 'access_token' : rawIdToken && rawIdToken.trim() !== '' ? 'id_token' : 'none'
        });
        throw new Error('No Cognito access token available. This may happen on first login if ALB headers are not yet set.');
      }

      // Prepare request body
      const requestBody = {
        tenant_id: tenantId,
        ...(isDevOrLocal && system_distinguisher ? {
          system_distinguisher
        } : {})
      };

      // Call switch-tenant API
      const response = await fetch('/api/csm/switch-tenant', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${cognitoAccessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to switch tenant');
      }
      const result = await response.json();

      // Close popover before redirect (parity with deprecated TenantSwitcher)
      setIsOpen(false);

      // Use redirect_url_with_logout to force ALB session clear for same-org tenant switches
      if (result.redirect_url_with_logout) {
        window.location.href = result.redirect_url_with_logout;
      } else if (result.redirect_url) {
        window.location.href = result.redirect_url;
      } else {
        window.location.href = window.location.pathname + window.location.search;
      }
    } catch (err) {
      console.error('Error switching tenant:', err);
      setError(err instanceof Error ? err.message : 'Failed to switch tenant');
      setSwitchingTenantId(null);
    }
  }, [currentTenantId]);
  useEffect(() => {
    if (isCSM && featureFlags?.enableTenantSwitcher && accessibleTenants === null && !isLoadingTenants) {
      loadAccessibleTenants();
    }
  }, [isCSM, featureFlags?.enableTenantSwitcher, accessibleTenants, isLoadingTenants, loadAccessibleTenants]);

  // Sort tenants: current tenant at top, then alphabetically by name
  const sortedTenants = useMemo(() => {
    if (!accessibleTenants || accessibleTenants.length === 0) {
      return accessibleTenants ?? [];
    }
    const sorted = [...accessibleTenants].sort((a, b) => {
      const nameA = (a.tenant.tenant_name || '').toLowerCase();
      const nameB = (b.tenant.tenant_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    if (currentTenantId) {
      const idx = sorted.findIndex(access => access.tenant.id === currentTenantId);
      if (idx !== -1) {
        const [current] = sorted.splice(idx, 1);
        return [current, ...sorted];
      }
    }
    return sorted;
  }, [accessibleTenants, currentTenantId]);

  // Filter tenants by search query (name and code)
  const filteredTenants = useMemo(() => {
    if (!searchQuery.trim()) return sortedTenants;
    const q = searchQuery.trim().toLowerCase();
    return sortedTenants.filter(access => {
      const name = (access.tenant.tenant_name || '').toLowerCase();
      const code = (access.tenant.tenant_code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [sortedTenants, searchQuery]);

  // Don't render if not CSM or feature flag is disabled
  if (!isCSM || !featureFlags?.enableTenantSwitcher) {
    return null;
  }
  return <Popover isOpen={isOpen} onOpenChange={setIsOpen} placement={featureFlags?.enableMediaAndSourceShare ? "top-start" : "bottom-end"} showArrow={true} triggerScaleOnOpen={false} classNames={{
    base: 'mr-0',
    content: 'flex flex-col justify-start items-start text-gray-900 bg-white p-4 rounded-md max-h-[calc(100vh-150px)] overflow-hidden overflow-x-hidden ml-[10px]'
  }} data-sentry-element="Popover" data-sentry-component="SwitchTenant" data-sentry-source-file="switch-tenant.tsx">
      <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="switch-tenant.tsx">
        <button type="button" aria-label="Switch Tenant" aria-haspopup="dialog" className="bg-transparent border-none p-0 hover:cursor-pointer">
          <UserIcon size={22} weight="duotone" data-sentry-element="UserIcon" data-sentry-source-file="switch-tenant.tsx" />
        </button>
      </PopoverTrigger>
      <PopoverContent data-sentry-element="PopoverContent" data-sentry-source-file="switch-tenant.tsx">
        <div className="flex flex-col gap-1 w-full">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div>
              <h2 className="text-2xl font-bold my-[5px]">Switch Tenant</h2>
            </div>
            {currentOrgName && <div className="flex items-center gap-1 text-xs text-gray-500">
                <BuildingsIcon size={12} />
                <span>{isSuperAdmin ? 'Super Admin' : 'CSM'}</span>
              </div>}
          </div>

          {/* Error message */}
          {error && <div className="mb-2 p-2 text-xs text-red-600 bg-red-50 rounded border border-red-200">
              <div className="flex items-center justify-between gap-2">
                <span>{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 underline text-xs">
                  Dismiss
                </button>
              </div>
            </div>}

          {/* Search/filter */}
          {!isLoadingTenants && sortedTenants.length > 0 && <div className="w-full">
              <Input type="search" placeholder="Search tenants..." value={searchQuery} onValueChange={setSearchQuery} size="sm" startContent={<MagnifyingGlassIcon size={16} className="text-gray-400" />} classNames={{
            input: 'px-0 bg-transparent',
            inputWrapper: 'px-0 mb-2 bg-slate-100 border border-slate-200 hover:bg-slate-50'
          }} aria-label="Filter tenants by name or code" />
            </div>}

          {/* Tenant list */}
          <div className="flex flex-col rounded-md bg-slate-900 overflow-y-auto overflow-x-hidden max-h-[400px] custom-scrollbar2">
            {isLoadingTenants ? <div className="flex items-center justify-center py-8 text-white/60 text-sm">
                <div className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white/60 rounded-full mr-0" />
                Loading tenants...
              </div> : filteredTenants.length === 0 ? <div className="flex items-center justify-center py-8 text-white/60 text-sm">
                {searchQuery.trim() ? 'No tenants match your search' : 'No tenants available'}
              </div> : filteredTenants.map(access => {
            const isCurrentTenant = access.tenant.id === currentTenantId;
            const isSwitching = switchingTenantId === access.tenant.id;
            const isDisabled = isCurrentTenant || !access.is_active || switchingTenantId !== null;
            return <button key={access.tenant.id} type="button" disabled={isDisabled} onClick={() => handleSwitchTenant(access.tenant.id)} className={`
                      box-border flex items-center justify-between gap-1 px-2 py-2 text-left w-full
                      transition-colors duration-150
                      ${isCurrentTenant ? 'text-white' : 'text-white/80 hover:bg-[#2d8dfe]/10 hover:text-white'}
                      ${isDisabled && !isCurrentTenant ? 'opacity-50 cursor-not-allowed' : ''}
                      ${!isDisabled ? 'cursor-pointer' : ''}
                    `}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {access.tenant.tenant_name}
                      </div>
                      {access.tenant.tenant_code && <div className="text-xs text-white/50 truncate">
                          {access.tenant.tenant_code}
                        </div>}
                      {!access.is_active && <div className="text-xs text-yellow-400 mt-0.5">Access inactive</div>}
                    </div>
                    <div className="flex flex-row items-center justify-end w-[60px]">
                      {isSwitching ? <div>
                          <div className="animate-spin h-4 w-4 border-2 border-white/20 border-t-white/60 rounded-full" />
                        </div> : isCurrentTenant ? <div>
                          <Checkbox isSelected={true} isReadOnly color="success" radius="sm" classNames={{
                    wrapper: 'bg-green-500/50'
                  }} />
                        </div> : null}
                    </div>
                  </button>;
          })}
          </div>

          {/* Footer with manage link */}
          {isSuperAdmin && HEIMDALL_URL && <div className="mt-2">
              <a href={`${HEIMDALL_URL}/super-admin`} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 hover:text-purple-800 underline">
                Manage Tenants →
              </a>
            </div>}
        </div>
      </PopoverContent>
    </Popover>;
}
export default SwitchTenant;